import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, Smartphone, Box } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import ProjectParametersSection from './ProjectParametersSection';
import ContentParametersSection from './ContentParametersSection';
import BackendOptionsSection from './BackendOptionsSection';
import DevelopmentOptionsSection from './DevelopmentOptionsSection';
import MaintenanceOptionsSection from './MaintenanceOptionsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета стоимости AR-технологий
 * Позволяет пользователю указать параметры проекта и рассчитать примерную стоимость
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  costBreakdown,
  setCostBreakdown,
  projectTypes,
  projectScopes,
  contentTypes,
  designComplexities,
  developmentRegions,
  developmentTeams,
  backendComplexities,
  maintenanceLevels,
  saveProject 
}) => {
  
  // Состояние для имени сохраняемого проекта
  const [saveProjectName, setSaveProjectName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем объем проекта автоматически при изменении типа проекта
      if (name === 'projectType') {
        const selectedProjectType = projectTypes.find(type => type.id === fieldValue);
        if (selectedProjectType) {
          newState.projectScope = selectedProjectType.defaultScope;
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeBackend' && !checked) {
        newState.backendComplexity = 'simple';
        newState.includeCMS = false;
        newState.includeAnalytics = false;
      }
      
      if (name === 'includeMaintenancePlan' && !checked) {
        newState.maintenanceDuration = '12';
        newState.maintenanceLevel = 'basic';
      }
      
      if (name === 'includeLocalization' && !checked) {
        newState.localizationLanguages = '1';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [projectTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      projectType, projectScope, platformCount, featureCount, timelineMonths,
      objectCount, designComplexity, contentType,
      includeBackend, backendComplexity, 
      includeLocalization, localizationLanguages,
      includeMaintenancePlan, maintenanceDuration, maintenanceLevel, 
      developmentRegion, developmentTeam
    } = formData;

    // Парсинг числовых значений
    const numPlatformCount = parseIntOrDefault(platformCount, -1);
    const numFeatureCount = parseIntOrDefault(featureCount, -1);
    const numObjectCount = parseIntOrDefault(objectCount, -1);
    const numTimelineMonths = parseFloatOrDefault(timelineMonths, -1);
    const numLocalizationLanguages = parseIntOrDefault(localizationLanguages, -1);
    const numMaintenanceDuration = parseIntOrDefault(maintenanceDuration, -1);

    // Валидация базовых полей
    if (!projectType) newErrors.projectType = 'Выберите тип проекта.';
    if (!projectScope) newErrors.projectScope = 'Выберите объем проекта.';
    if (!designComplexity) newErrors.designComplexity = 'Выберите сложность дизайна.';
    if (!contentType) newErrors.contentType = 'Выберите тип контента.';
    if (!developmentRegion) newErrors.developmentRegion = 'Выберите регион разработки.';
    if (!developmentTeam) newErrors.developmentTeam = 'Выберите тип команды.';
    
    if (numPlatformCount <= 0) newErrors.platformCount = 'Должна быть хотя бы одна платформа.';
    if (!newErrors.platformCount && numPlatformCount > 10) newErrors.platformCount = 'Максимум 10 платформ.';
    
    if (numFeatureCount <= 0) newErrors.featureCount = 'Должна быть хотя бы одна функция.';
    if (!newErrors.featureCount && numFeatureCount > 30) newErrors.featureCount = 'Максимум 30 функций.';
    
    if (numObjectCount <= 0) newErrors.objectCount = 'Должен быть хотя бы один объект.';
    if (!newErrors.objectCount && numObjectCount > 200) newErrors.objectCount = 'Максимум 200 объектов.';
    
    if (numTimelineMonths <= 0) newErrors.timelineMonths = 'Минимальный срок - 1 месяц.';
    if (!newErrors.timelineMonths && numTimelineMonths > 36) newErrors.timelineMonths = 'Максимальный срок - 36 месяцев.';

    // Валидация бэкенда
    if (includeBackend) {
      if (!backendComplexity) newErrors.backendComplexity = 'Выберите сложность бэкенда.';
    }

    // Валидация локализации
    if (includeLocalization) {
      if (numLocalizationLanguages <= 0) newErrors.localizationLanguages = 'Минимум 1 язык.';
      if (!newErrors.localizationLanguages && numLocalizationLanguages > 50) newErrors.localizationLanguages = 'Максимум 50 языков.';
    }

    // Валидация плана обслуживания
    if (includeMaintenancePlan) {
      if (numMaintenanceDuration <= 0) newErrors.maintenanceDuration = 'Минимум 1 месяц.';
      if (!newErrors.maintenanceDuration && numMaintenanceDuration > 60) newErrors.maintenanceDuration = 'Максимум 60 месяцев.';
      if (!maintenanceLevel) newErrors.maintenanceLevel = 'Выберите уровень обслуживания.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета стоимости AR-проекта
  const calculateProjectCost = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setCostBreakdown([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const { 
          projectType, projectScope, platformCount, featureCount, objectCount,
          designComplexity, contentType, timelineMonths,
          includeBackend, backendComplexity, includeCMS, includeAnalytics,
          includeLocalization, localizationLanguages,
          includeMaintenancePlan, maintenanceDuration, maintenanceLevel,
          developmentRegion, developmentTeam, hasExistingAssets
        } = formData;

        // Получаем базовые множители из конфигурации
        const selectedProjectType = projectTypes.find(t => t.id === projectType);
        const selectedScope = projectScopes.find(s => s.id === projectScope);
        const selectedDesign = designComplexities.find(d => d.id === designComplexity);
        const selectedContent = contentTypes.find(c => c.id === contentType);
        const selectedRegion = developmentRegions.find(r => r.id === developmentRegion);
        const selectedTeam = developmentTeams.find(t => t.id === developmentTeam);
        
        // Парсинг числовых значений
        const numPlatformCount = parseIntOrDefault(platformCount, 1);
        const numFeatureCount = parseIntOrDefault(featureCount, 1);
        const numObjectCount = parseIntOrDefault(objectCount, 1);
        const numTimelineMonths = parseFloatOrDefault(timelineMonths, 3);
        const numLocalizationLanguages = parseIntOrDefault(localizationLanguages, 1);
        const numMaintenanceDuration = parseIntOrDefault(maintenanceDuration, 12);
        
        // Базовая стоимость в зависимости от типа проекта
        const basePrice = selectedProjectType?.basePrice || 15000;
        
        // --- Расчет стоимости разработки ---
        // Базовая стоимость * множитель объема * множитель региона * множитель команды
        // * (1 + корректировка за количество платформ) * (1 + корректировка за количество функций)
        const platformAdjustment = Math.log10(numPlatformCount) * 0.5; // Логарифмический рост за дополнительные платформы
        const featureAdjustment = Math.log10(numFeatureCount) * 0.7; // Логарифмический рост за дополнительные функции
        
        const developmentCost = basePrice 
          * (selectedScope?.factor || 1) 
          * (selectedRegion?.factor || 1) 
          * (selectedTeam?.factor || 1)
          * (1 + platformAdjustment)
          * (1 + featureAdjustment);
        
        // --- Расчет стоимости дизайна ---
        // Базовая стоимость дизайна * множитель сложности дизайна
        const designBaseCost = basePrice * 0.3; // Дизайн обычно составляет около 30% от базовой стоимости
        const designCost = designBaseCost * (selectedDesign?.factor || 1);
        
        // --- Расчет стоимости контента ---
        // Базовая стоимость контента * множитель типа контента * (1 + корректировка за количество объектов)
        const contentBaseCost = basePrice * 0.25; // Контент обычно составляет около 25% от базовой стоимости
        const objectAdjustment = Math.log10(numObjectCount) * 0.8; // Логарифмический рост за дополнительные объекты
        const contentCost = contentBaseCost 
          * (selectedContent?.factor || 1) 
          * (1 + objectAdjustment)
          * (hasExistingAssets ? 0.7 : 1); // Скидка 30% если есть существующие ассеты
        
        // --- Расчет стоимости бэкенда ---
        let backendCost = 0;
        if (includeBackend) {
          const selectedBackend = backendComplexities.find(b => b.id === backendComplexity);
          const backendFactor = selectedBackend?.factor || 1;
          
          // Базовая стоимость бэкенда * множитель сложности
          const backendBaseCost = basePrice * 0.4; // Бэкенд может составлять до 40% от базовой стоимости
          backendCost = backendBaseCost * backendFactor;
          
          // Дополнительные затраты на CMS и аналитику
          if (includeCMS) backendCost += backendBaseCost * 0.2;
          if (includeAnalytics) backendCost += backendBaseCost * 0.15;
        }
        
        // --- Расчет стоимости локализации ---
        let localizationCost = 0;
        if (includeLocalization && numLocalizationLanguages > 1) {
          // Базовая стоимость за язык * (количество языков - 1) * множитель сложности контента
          const costPerLanguage = basePrice * 0.05; // 5% от базовой стоимости за каждый дополнительный язык
          localizationCost = costPerLanguage * (numLocalizationLanguages - 1) * (selectedContent?.factor || 1);
        }
        
        // --- Расчет стоимости обслуживания ---
        let maintenanceCost = 0;
        if (includeMaintenancePlan) {
          const selectedMaintenance = maintenanceLevels.find(m => m.id === maintenanceLevel);
          const maintenanceFactor = selectedMaintenance?.factor || 0.1;
          
          // (Разработка + Дизайн + Контент + Бэкенд) * множитель уровня обслуживания * количество месяцев / 12
          const totalProjectCost = developmentCost + designCost + contentCost + backendCost;
          maintenanceCost = totalProjectCost * maintenanceFactor * (numMaintenanceDuration / 12);
        }
        
        // --- Расчет общей стоимости проекта ---
        const subtotal = developmentCost + designCost + contentCost + backendCost + localizationCost;
        const totalCost = subtotal + maintenanceCost;
        
        // --- Корректировка на сроки ---
        // Если срок меньше стандартного, увеличиваем стоимость
        // Если срок больше стандартного, уменьшаем стоимость
        const standardTimelineMonths = 6; // Стандартный срок для среднего проекта
        let timelineAdjustedCost = totalCost;
        
        if (numTimelineMonths < standardTimelineMonths) {
          // Увеличение стоимости при сжатых сроках (до +50% при минимальных сроках)
          const urgencyFactor = 1 + (0.5 * (1 - (numTimelineMonths / standardTimelineMonths)));
          timelineAdjustedCost = totalCost * urgencyFactor;
        } else if (numTimelineMonths > standardTimelineMonths) {
          // Небольшое снижение стоимости при увеличенных сроках (до -20% при максимальных сроках)
          const relaxedFactor = 1 - (0.2 * Math.min(1, (numTimelineMonths - standardTimelineMonths) / 12));
          timelineAdjustedCost = totalCost * relaxedFactor;
        }
        
        // --- Подготовка результата ---
        const categoryCosts = {
          development: Math.round(developmentCost),
          design: Math.round(designCost),
          content: Math.round(contentCost),
          backend: Math.round(backendCost),
          localization: Math.round(localizationCost),
          maintenance: Math.round(maintenanceCost)
        };
        
        const rawCosts = {
          subtotal: Math.round(subtotal),
          total: Math.round(totalCost),
          timelineAdjusted: Math.round(timelineAdjustedCost)
        };
        
        // Подготавливаем детализацию стоимости для отображения
        const breakdown = Object.entries(categoryCosts)
          .filter(([_, cost]) => cost > 0)
          .map(([category, cost]) => ({
            category,
            cost,
            percentage: (cost / timelineAdjustedCost) * 100
          }))
          .sort((a, b) => b.cost - a.cost);
        
        const results = {
          projectType: selectedProjectType?.name || projectType,
          projectScope: selectedScope?.name || projectScope,
          timelineMonths: numTimelineMonths,
          categoryCosts,
          subtotal: rawCosts.subtotal,
          maintenanceCost: categoryCosts.maintenance,
          totalCost: rawCosts.timelineAdjusted,
          timelineFactor: rawCosts.timelineAdjusted / rawCosts.total,
          estimatedHours: Math.round(timelineAdjustedCost / 100), // Примерно $100 в час
          platforms: numPlatformCount,
          features: numFeatureCount
        };
        
        setResults(results);
        setCostBreakdown(breakdown);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setCostBreakdown([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setCostBreakdown, setIsCalculating, projectTypes, projectScopes, designComplexities, contentTypes, developmentRegions, developmentTeams, backendComplexities, maintenanceLevels]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      projectType: 'mobile',
      projectScope: 'small',
      platformCount: '1',
      featureCount: '3',
      designComplexity: 'medium',
      contentType: 'basic',
      objectCount: '5',
      includeBackend: false,
      backendComplexity: 'simple',
      includeCMS: false,
      includeAnalytics: false,
      includeLocalization: false,
      localizationLanguages: '1',
      developmentRegion: 'east_europe',
      developmentTeam: 'mixed',
      timelineMonths: '3',
      hasExistingAssets: false,
      includeMaintenancePlan: false,
      maintenanceDuration: '12',
      maintenanceLevel: 'basic',
    });
    setResults(null);
    setCostBreakdown([]);
    setErrors({});
    setSaveProjectName('');
    setShowSaveDialog(false);
  };

  // Экспорт данных расчета в CSV
  const exportToCSV = () => {
    if (!results || !costBreakdown || costBreakdown.length === 0) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки и строки
    const headers = ['Категория', 'Стоимость ($)', 'Процент от общей стоимости (%)'];
    
    const formatCsvNumber = (num) => String(Math.round(num)).replace('.', ',');
    const formatCsvPercent = (num) => String(Math.round(num * 10) / 10).replace('.', ',');

    const rows = costBreakdown.map(item => {
      const categoryName = getCategoryDisplayName(item.category);
      return [
        categoryName,
        formatCsvNumber(item.cost),
        formatCsvPercent(item.percentage)
      ].join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      'ИТОГО:', 
      formatCsvNumber(results.totalCost),
      '100,0'
    ].join(';');
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Тип проекта:', results.projectType],
      ['Объем проекта:', results.projectScope],
      ['Срок проекта:', `${results.timelineMonths} мес.`],
      ['Количество платформ:', results.platforms],
      ['Количество функций:', results.features],
      ['Общая стоимость:', `${formatCsvNumber(results.totalCost)}`],
      ['Примерное количество часов:', results.estimatedHours],
      ['']  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_стоимости_AR_проекта.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Получение названия категории для отображения
  const getCategoryDisplayName = (category) => {
    const categoryMap = {
      development: 'Разработка',
      design: 'Дизайн',
      content: 'Контент и 3D-модели',
      backend: 'Бэкенд и серверная часть',
      localization: 'Локализация',
      maintenance: 'Обслуживание и поддержка'
    };
    return categoryMap[category] || category;
  };

  // Обработчик сохранения проекта
  const handleSaveProject = () => {
    if (showSaveDialog) {
      const savedResult = saveProject(saveProjectName || null);
      if (savedResult) {
        alert(`Проект "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveProjectName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateProjectCost();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Smartphone className="w-6 h-6 text-indigo-500" />
          Параметры AR-проекта
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры проекта */}
        <ProjectParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          projectTypes={projectTypes}
          projectScopes={projectScopes}
        />

        {/* Параметры контента */}
        <ContentParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          contentTypes={contentTypes}
          designComplexities={designComplexities}
        />

        {/* Опции бэкенда */}
        <BackendOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          backendComplexities={backendComplexities}
        />

        {/* Опции разработки */}
        <DevelopmentOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          developmentRegions={developmentRegions}
          developmentTeams={developmentTeams}
        />

        {/* Опции обслуживания */}
        <MaintenanceOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          maintenanceLevels={maintenanceLevels}
        />

        {/* Диалог сохранения проекта */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveProjectName}
                onChange={(e) => setSaveProjectName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveProject}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать стоимость'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {costBreakdown.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать отчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveProject}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;